import React, { useState, useEffect } from 'react';
import { X, Crop, Plus, Trash2 } from 'lucide-react';
import { StoreImageCropModal } from '../components/StoreImageCropModal';
import { createProduct, updateProduct } from '../hooks/useStoreApi';
import { PRODUCT_LABEL_COLORS, DEFAULT_PRODUCT_LABEL_COLOR, getProductLabelColor } from '../utils/productLabel';
import { getStoreThemeStyle } from '../themes/applyStoreTheme';
import { resolveDesign } from '../themes/storeDesign';
import { useVocab, useIsAppointments } from '../hooks/useVocab';

// Local API base (duplicated from StoreAdmin until central config is made)
const API_BASE_URL = process.env.REACT_APP_STORE_API_URL || 'https://v3ugnvc3qk.execute-api.us-west-2.amazonaws.com/prod';

export const ProductModal = ({ storeId, product, categories, onClose, onSave, chatId, store }) => {
    // Resolve the store's color scheme so the label color swatches preview in
    // the store's actual palette (the admin UI lives outside the storefront's
    // [data-store-theme] subtree, so we scope a wrapper for the picker below).
    const themeId = resolveDesign(store || {}).themeId;
    const t = useVocab();
    const isAppt = useIsAppointments();
    // A catalog item in a practice is a bookable service; in a shop it's a product.
    // Existing items keep their own kind; new items default to the store's nature.
    const isService = (product?.kind || (isAppt ? 'service' : 'product')) === 'service';
    const [formData, setFormData] = useState({
        name: product?.name || '',
        description: product?.description || '',
        price: product?.price || '',
        category_id: product?.category_id || '',
        image: null, // new image base64 (only if user picked a replacement)
        // Inventory form state (normalized)
        inventory_enabled: !!product?.inventory?.enabled,
        inventory_quantity: product?.inventory?.enabled ? (product?.inventory?.quantity ?? '') : '',
        // Delivery relevance (defaults to true if not specified)
        requires_delivery: product?.requires_delivery !== undefined ? product.requires_delivery : true,
        // Booking attributes (services only)
        duration_minutes: product?.duration_minutes ? String(product.duration_minutes) : (isService ? '60' : ''),
        capacity: product?.capacity ? String(product.capacity) : '1',
        // Sale banner
        sale_enabled: !!product?.sale?.enabled,
        sale_percent: product?.sale?.percent ? String(product.sale.percent) : '',
        // Custom label on the product image (text + scheme color)
        label_text: product?.label?.text || '',
        label_color: product?.label?.color || DEFAULT_PRODUCT_LABEL_COLOR
    });
    // Extra options state (paid add-ons)
    const [extraOptions, setExtraOptions] = useState(product?.paid_options || []);
    // Preserve original image to detect whether user edited (changed or removed)
    const originalImage = product?.images?.[0] || null;
    const [imagePreview, setImagePreview] = useState(originalImage);
    const [imageChanged, setImageChanged] = useState(false); // true if user replaced or removed the original image
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [tempImageSrc, setTempImageSrc] = useState(null);
    // Removed advanced toggle: all fields always visible, rely on scroll for overflow

    // Prevent background scroll while modal open
    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, []);

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            setError('גודל התמונה גדול מדי. מקסימום 10MB');
            return;
        }
        if (!file.type.startsWith('image/')) {
            setError('יש להעלות קובץ תמונה בלבד');
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
            setTempImageSrc(reader.result);
            setShowCropModal(true);
            setError(null);
        };
        reader.readAsDataURL(file);
    };

    const handleCropComplete = (croppedImage) => {
        setFormData(prev => ({ ...prev, image: croppedImage }));
        setImagePreview(croppedImage);
        setImageChanged(true);
        setShowCropModal(false);
        setTempImageSrc(null);
    };

    // Extra options management
    const addExtraOption = () => {
        setExtraOptions(prev => [...prev, { name: '', price: '' }]);
    };

    const removeExtraOption = (index) => {
        setExtraOptions(prev => prev.filter((_, i) => i !== index));
    };

    const updateExtraOption = (index, field, value) => {
        setExtraOptions(prev => prev.map((option, i) =>
            i === index ? { ...option, [field]: value } : option
        ));
    };

    const uploadImageToS3 = async (base64Data) => {
        if (!base64Data) return null;
        const matches = base64Data.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
        if (!matches || matches.length !== 3) throw new Error('Invalid image format');
        const fileType = matches[1];
        const fileName = `product_${Date.now()}.${fileType.split('/')[1]}`;
        const urlResponse = await fetch(`${API_BASE_URL}/admin/store/${storeId}/upload-url`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ file_type: fileType, file_name: fileName, upload_type: 'product' })
        });
        if (!urlResponse.ok) throw new Error('Failed to get upload URL');
        const { upload_url, public_url } = await urlResponse.json();
        const blob = await fetch(base64Data).then(r => r.blob());
        const uploadResponse = await fetch(upload_url, { method: 'PUT', headers: { 'Content-Type': fileType }, body: blob });
        if (!uploadResponse.ok) throw new Error('Failed to upload image');
        return public_url;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const productData = {
                name: formData.name,
                description: formData.description,
                price: parseFloat(formData.price),
                kind: isService ? 'service' : 'product',
                // Services are delivered in person — never shipped.
                requires_delivery: isService ? false : formData.requires_delivery,
                sale: (() => {
                    const pct = parseInt(formData.sale_percent, 10);
                    const safePct = Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : 0;
                    return {
                        enabled: !!formData.sale_enabled && safePct > 0,
                        percent: safePct
                    };
                })()
            };
            if (formData.category_id) productData.category_id = formData.category_id;
            // Custom label: empty text clears it; otherwise store text + scheme color.
            productData.label = { text: (formData.label_text || '').trim(), color: formData.label_color };
            // Image handling logic:
            // Requirement: If image NOT edited (unchanged), do NOT send `images` field in update (backend should keep existing)
            // If user explicitly changed (new upload) or removed the image, mark imageChanged and reflect that.
            if (imageChanged) {
                if (formData.image) {
                    // User picked/replaced image -> upload new and send
                    const imageUrl = await uploadImageToS3(formData.image);
                    productData.images = [imageUrl];
                } else {
                    // User removed existing image without choosing a new one -> send empty array to clear
                    productData.images = [];
                }
            } // else do not include images field (keeps backend value as-is)
            if (isService) {
                // Booking attributes replace physical inventory for a service.
                const dur = parseInt(formData.duration_minutes, 10);
                productData.duration_minutes = Number.isFinite(dur) && dur > 0 ? dur : null;
                const cap = parseInt(formData.capacity, 10);
                productData.capacity = Number.isFinite(cap) && cap > 0 ? cap : 1;
                productData.inventory = { enabled: false };
            } else {
                // Inventory mapping
                if (formData.inventory_enabled) {
                    const qtyVal = parseInt(formData.inventory_quantity, 10);
                    if (isNaN(qtyVal) || qtyVal < 0) {
                        throw new Error('מלאי חייב להיות מספר אי-שלילי');
                    }
                    productData.inventory = { enabled: true, quantity: qtyVal };
                } else {
                    productData.inventory = { enabled: false };
                }
            }
            // Extra options (paid_options) - filter out empty entries and validate
            const validExtraOptions = extraOptions
                .filter(opt => opt.name && opt.price)
                .map(opt => ({
                    name: opt.name,
                    price: parseFloat(opt.price)
                }));
            if (validExtraOptions.length > 0) {
                productData.paid_options = validExtraOptions;
            } else {
                productData.paid_options = [];
            }
            if (product) {
                await updateProduct(storeId, product.product_id, productData, chatId);
            } else {
                await createProduct(storeId, productData, chatId);
            }
            onSave();
        } catch (err) {
            setError(err.message || `שגיאה בשמירת ${t.item === 'שירות' ? 'השירות' : 'המוצר'}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000] flex items-center justify-center p-4 pb-24 animate-fade-in" onClick={onClose}>
            {/* Dialog container: centered with space for footer, internal scroll region ensures full visibility */}
            <div
                role="dialog"
                aria-modal="true"
                aria-label={product ? `עריכת ${t.item}` : `יצירת ${t.item} חדש`}
                className="bg-white rounded-xl w-full max-w-[400px] md:max-w-md shadow-elevated animate-scale-in flex flex-col max-h-full overflow-hidden border border-mekomit-100"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Fixed header */}
                <div className="flex items-center justify-between px-3 py-2 md:px-4 md:py-3 border-b border-mekomit-100 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 shrink-0">
                    <div>
                        <h3 className="text-base md:text-lg font-bold text-mekomit-900 leading-tight">{product ? `ערוך ${t.item}` : `${t.item} חדש`}</h3>
                        <p className="hidden md:block text-xs text-mekomit-600 mt-0.5">מלא את הפרטים</p>
                    </div>
                    <button aria-label="סגור" onClick={onClose} className="p-1.5 hover:bg-mekomit-100 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-warning-500">
                        <X className="w-4 h-4 text-mekomit-700" />
                    </button>
                </div>
                {/* Scrollable content area */}
                <div className="px-3 py-3 md:px-4 md:py-4 overflow-y-auto flex-1">
                    {error && <div className="mb-3 p-2 md:p-3 bg-danger-50 border border-danger-200 rounded-md text-danger-800 text-[11px] md:text-sm leading-snug">{error}</div>}
                    <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4 text-[13px] md:text-sm" id="product-form">
                        <div>
                            <label className="block text-[11px] md:text-sm font-medium text-ink mb-1">{`תמונת ${t.item}`} <span className="text-[10px] md:text-xs text-ink-subtle">(אופציונלי)</span></label>
                            <div className="flex items-center gap-2 md:gap-4">
                                {imagePreview && (
                                    <div className="relative w-16 h-16 md:w-24 md:h-24 rounded-md overflow-hidden border border-line-strong">
                                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" loading="lazy" />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setImagePreview(null);
                                                setFormData(prev => ({ ...prev, image: null }));
                                                // Mark as changed only if there was an original image (removal counts as edit)
                                                if (originalImage) setImageChanged(true);
                                            }}
                                            className="absolute top-1 right-1 p-1 bg-danger-500/80 text-white rounded-full hover:bg-danger-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-danger-500"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                )}
                                <label className="flex-1 cursor-pointer">
                                    <div className="px-2 py-2 border border-dashed border-line-strong rounded-md hover:border-warning-500 transition-colors text-center flex items-center justify-center gap-2 text-[11px] md:text-sm">
                                        <Crop className="w-4 h-4 text-ink-subtle shrink-0" />
                                        <span className="text-ink-muted">{imagePreview ? 'שנה תמונה' : 'בחר תמונה'}</span>
                                    </div>
                                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                                </label>
                            </div>
                            <p className="text-[10px] md:text-xs text-ink-subtle mt-1 leading-tight">נחתך ודחוס אוטומטית</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 md:gap-4">
                            <div>
                                <label className="block text-[11px] md:text-sm font-medium text-ink mb-1">שם</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-2.5 py-1.5 md:px-3 md:py-2 border border-line-strong rounded-md focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong text-[13px] md:text-sm"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] md:text-sm font-medium text-ink mb-1">מחיר (₪)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.price}
                                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                                    className="w-full px-2.5 py-1.5 md:px-3 md:py-2 border border-line-strong rounded-md focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong text-[13px] md:text-sm"
                                    required
                                />
                            </div>
                        </div>
                        {/* Advanced fields always visible */}
                        <div>
                            <label className="block text-[11px] md:text-sm font-medium text-ink mb-1">תיאור</label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                className="w-full px-2.5 py-1.5 md:px-3 md:py-2 border border-line-strong rounded-md focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong text-[13px] md:text-sm resize-y min-h-[70px]"
                                rows="3"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] md:text-sm font-medium text-ink mb-1">קטגוריה</label>
                            <select
                                value={formData.category_id}
                                onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                                className="w-full px-2.5 py-1.5 md:px-3 md:py-2 border border-line-strong rounded-md focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong text-[13px] md:text-sm"
                            >
                                <option value="">ללא קטגוריה</option>
                                {categories?.map(category => (
                                    <option key={category.category_id} value={category.category_id}>{category.name}</option>
                                ))}
                            </select>
                        </div>
                        {/* Service booking attributes (practice) — replace physical inventory/delivery */}
                        {isService && (
                            <div className="grid grid-cols-2 gap-2 md:gap-4">
                                <div>
                                    <label className="block text-[11px] md:text-sm font-medium text-ink mb-1">משך הפגישה (דקות)</label>
                                    <input
                                        type="number"
                                        min="5"
                                        step="5"
                                        value={formData.duration_minutes}
                                        onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: e.target.value }))}
                                        className="w-full px-2.5 py-1.5 md:px-3 md:py-2 border border-line-strong rounded-md focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong text-[13px] md:text-sm"
                                        placeholder="למשל 60"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[11px] md:text-sm font-medium text-ink mb-1">מקומות במועד</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={formData.capacity}
                                        onChange={(e) => setFormData(prev => ({ ...prev, capacity: e.target.value }))}
                                        className="w-full px-2.5 py-1.5 md:px-3 md:py-2 border border-line-strong rounded-md focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong text-[13px] md:text-sm"
                                        placeholder="1"
                                    />
                                    <p className="text-[10px] md:text-xs text-ink-subtle mt-1 leading-tight">כמה מטופלים יכולים לקבוע את אותו מועד (1 = פגישה אישית)</p>
                                </div>
                            </div>
                        )}
                        {/* Inventory (shop only) */}
                        {!isService && (
                        <div className="grid grid-cols-2 gap-2 md:gap-4">
                            <div>
                                <label className="block text-[11px] md:text-sm font-medium text-ink mb-1">מלאי (כמות)</label>
                                <input
                                    type="number"
                                    min="0"
                                    disabled={!formData.inventory_enabled}
                                    value={formData.inventory_enabled ? formData.inventory_quantity : ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, inventory_quantity: e.target.value }))}
                                    className="w-full px-2.5 py-1.5 md:px-3 md:py-2 border border-line-strong rounded-md focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong text-[13px] md:text-sm disabled:bg-surface-muted disabled:text-ink-subtle"
                                    placeholder={formData.inventory_enabled ? 'הכנס כמות' : 'ללא הגבלה'}
                                />
                            </div>
                            <div className="flex items-end">
                                <label className="flex items-center gap-2 text-[11px] md:text-sm font-medium text-ink mb-1 w-full">
                                    <input
                                        type="checkbox"
                                        checked={!formData.inventory_enabled}
                                        onChange={(e) => setFormData(prev => ({ ...prev, inventory_enabled: !e.target.checked }))}
                                        className="h-4 w-4 rounded border-line-strong text-warning-600 focus:ring-warning-500"
                                    />
                                    <span>מלאי בלתי מוגבל</span>
                                </label>
                            </div>
                        </div>
                        )}
                        {/* Delivery relevance (shop only) */}
                        {!isService && (
                        <div>
                            <label className="flex items-center gap-2 text-[11px] md:text-sm font-medium text-ink">
                                <input
                                    type="checkbox"
                                    checked={formData.requires_delivery}
                                    onChange={(e) => setFormData(prev => ({ ...prev, requires_delivery: e.target.checked }))}
                                    className="h-4 w-4 rounded border-line-strong text-warning-600 focus:ring-warning-500"
                                />
                                <span>המוצר דורש משלוח \ איסוף</span>
                            </label>
                            <p className="text-[10px] md:text-xs text-ink-subtle mt-1 leading-tight">עבור מוצרים שאינם דורשים משלוח \ איסוף לא יידרש לבחור משלוח או איסוף בטופס סיום הזמנה</p>
                        </div>
                        )}
                        {/* Sale banner section */}
                        <div className="border-t border-line pt-3 md:pt-4">
                            <label className="flex items-center gap-2 text-[11px] md:text-sm font-medium text-ink">
                                <input
                                    type="checkbox"
                                    checked={formData.sale_enabled}
                                    onChange={(e) => setFormData(prev => ({ ...prev, sale_enabled: e.target.checked }))}
                                    className="h-4 w-4 rounded border-line-strong text-danger-600 focus:ring-danger-500"
                                />
                                <span>{`סמן ${t.item} במבצע`}</span>
                            </label>
                            <p className="text-[10px] md:text-xs text-ink-subtle mt-1 leading-tight">תופיע תווית "במבצע" על תמונת המוצר, והמחיר בסל יקטן בהתאם</p>
                            {formData.sale_enabled && (
                                <div className="mt-2">
                                    <label className="block text-[11px] md:text-sm font-medium text-ink mb-1">אחוז הנחה</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            min="1"
                                            max="100"
                                            step="1"
                                            value={formData.sale_percent}
                                            onChange={(e) => setFormData(prev => ({ ...prev, sale_percent: e.target.value.replace(/[^0-9]/g, '') }))}
                                            placeholder="למשל 10"
                                            className="w-24 px-2.5 py-1.5 md:px-3 md:py-2 border border-line-strong rounded-md focus:ring-2 focus:ring-danger-500 focus:border-transparent text-ink-strong text-[13px] md:text-sm"
                                        />
                                        <span className="text-[13px] md:text-sm text-ink">%</span>
                                    </div>
                                    <p className="text-[10px] md:text-xs text-ink-subtle mt-1 leading-tight">מספר שלם בין 1 ל-100. ההנחה תחול על מחיר המוצר (לא על תוספות בתשלום).</p>
                                </div>
                            )}
                        </div>
                        {/* Custom label section */}
                        <div className="border-t border-line pt-3 md:pt-4">
                            <label className="block text-[11px] md:text-sm font-medium text-ink mb-1">תווית על התמונה <span className="text-[10px] md:text-xs text-ink-subtle">(אופציונלי)</span></label>
                            <p className="text-[10px] md:text-xs text-ink-subtle mb-2 leading-tight">תווית קצרה שתוצג על תמונת המוצר (למשל "חדש", "מומלץ", "אזל בקרוב")</p>
                            <input
                                type="text"
                                maxLength={24}
                                value={formData.label_text}
                                onChange={(e) => setFormData(prev => ({ ...prev, label_text: e.target.value }))}
                                placeholder="טקסט התווית"
                                className="w-full px-2.5 py-1.5 md:px-3 md:py-2 border border-line-strong rounded-md focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong text-[13px] md:text-sm"
                            />
                            {formData.label_text.trim() && (
                                <div className="mt-2 p-2.5 rounded-md" data-store-theme style={getStoreThemeStyle(themeId)}>
                                    <label className="block text-[11px] md:text-sm font-medium text-ink mb-1">צבע התווית</label>
                                    <p className="text-[10px] md:text-xs text-ink-subtle mb-2 leading-tight">הצבעים נלקחים מערכת הצבעים של החנות</p>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {PRODUCT_LABEL_COLORS.map((c) => {
                                            const selected = formData.label_color === c.key;
                                            return (
                                                <button
                                                    type="button"
                                                    key={c.key}
                                                    onClick={() => setFormData(prev => ({ ...prev, label_color: c.key }))}
                                                    className={`w-8 h-8 rounded-full ${c.swatchClass} transition-transform ${selected ? 'ring-2 ring-offset-2 ring-ink scale-110' : 'ring-1 ring-line-strong hover:scale-105'}`}
                                                    title={c.name}
                                                    aria-label={c.name}
                                                    aria-pressed={selected}
                                                />
                                            );
                                        })}
                                    </div>
                                    {/* Live preview of the label as it will appear on the product image */}
                                    <div className="mt-3">
                                        <span className="block text-[10px] md:text-xs text-ink-subtle mb-1">תצוגה מקדימה</span>
                                        <span className={`inline-block ${getProductLabelColor(formData.label_color).badgeClass} px-2 py-0.5 rounded-md text-[10px] font-bold shadow-sm max-w-[10rem] truncate`}>
                                            {formData.label_text.trim()}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* Extra options section */}
                        <div className="border-t border-line pt-3 md:pt-4">
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-[11px] md:text-sm font-medium text-ink">תוספות בתשלום</label>
                                <button
                                    type="button"
                                    onClick={addExtraOption}
                                    className="flex items-center gap-1 px-2 py-1 text-[11px] md:text-xs text-warning-600 hover:text-warning-700 hover:bg-warning-50 rounded transition-colors"
                                >
                                    <Plus className="w-3 h-3" />
                                    <span>הוסף תוספת</span>
                                </button>
                            </div>
                            {extraOptions.length === 0 ? (
                                <p className="text-[10px] md:text-xs text-ink-subtle italic">אין תוספות. לחץ "הוסף תוספת" כדי להוסיף.</p>
                            ) : (
                                <div className="space-y-2">
                                    {extraOptions.map((option, index) => (
                                        <div key={index} className="flex gap-2 items-start">
                                            <div className="flex-1 grid grid-cols-2 gap-2">
                                                <input
                                                    type="text"
                                                    value={option.name}
                                                    onChange={(e) => updateExtraOption(index, 'name', e.target.value)}
                                                    placeholder="שם תוספת"
                                                    className="w-full px-2.5 py-1.5 md:px-3 md:py-2 border border-line-strong rounded-md focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong text-[13px] md:text-sm"
                                                />
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={option.price}
                                                    onChange={(e) => updateExtraOption(index, 'price', e.target.value)}
                                                    placeholder="מחיר (₪)"
                                                    className="w-full px-2.5 py-1.5 md:px-3 md:py-2 border border-line-strong rounded-md focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong text-[13px] md:text-sm"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeExtraOption(index)}
                                                className="p-1.5 md:p-2 text-danger-600 hover:bg-danger-50 rounded transition-colors mt-0.5"
                                                aria-label="מחק תוספת"
                                            >
                                                <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </form>
                </div>
                {/* Fixed footer with action buttons */}
                <div className="border-t border-mekomit-100 bg-white/95 backdrop-blur px-3 py-3 md:px-4 md:py-3 shrink-0">
                    <div className="flex flex-col md:flex-row gap-2 md:gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-full md:flex-1 px-3 py-2 border border-line-strong rounded-md hover:bg-surface-muted transition-colors text-ink-strong text-[13px] md:text-sm disabled:opacity-50"
                            disabled={loading}
                        >ביטול</button>
                        <button
                            type="submit"
                            form="product-form"
                            className="w-full md:flex-1 px-3 py-2 bg-warning-500 text-white rounded-md hover:bg-warning-600 transition-colors text-[13px] md:text-sm disabled:opacity-50"
                            disabled={loading}
                        >{loading ? 'שומר...' : (product ? 'שמור' : 'הוסף')}</button>
                    </div>
                </div>
                {showCropModal && tempImageSrc && (
                    <StoreImageCropModal
                        imageSrc={tempImageSrc}
                        onClose={() => { setShowCropModal(false); setTempImageSrc(null); }}
                        onCropComplete={handleCropComplete}
                        aspect={1}
                        mode="product"
                    />
                )}
            </div>
        </div>
    );
};

export default ProductModal;
